import * as React from 'react';
import { createContext, useContext, useMemo } from 'react';
import { onRender } from './intentTypes';
import { createView, View } from './view';
import { createCss, deprecatedCss } from './deprecatedCss';
import { StyleSheets } from '../stylesheets';
import { onComponentWillReceiveProps } from './view/transformProps';
export function isThemedIntent(intent) {
    return !!intent.byTheme;
}
export function createTheme(id, css) {
    function theme(intent, intents) {
        if (intent.byTheme[id]) {
            intent.byTheme[id].push(...intents);
        } else {
            intent.byTheme[id] = intents.slice();
        }
        return intent;
    }
    theme.css = css.configure({
        layer: `theme.${id}`
    });
    StyleSheets.insert([], theme.css.options.layer);
    theme.deprecatedCss = createCss(deprecatedCss.plugins, {
        layer: `theme.${id}`
    });
    theme.attributes = (intents)=>// eslint-disable-next-line @typescript-eslint/no-use-before-define
        theme(themeAttributes, intents);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    theme.global = (intents)=>theme(themeGlobals, intents);
    theme.id = id;
    return theme;
}
const ThemeContext = /*#__PURE__*/ createContext([]);
function getIntentsForThemes(themes, intent) {
    const intents = [];
    function flatten(current) {
        themes.forEach((theme)=>{
            const themeIntents = current.byTheme[theme.id];
            if (!themeIntents) {
                return;
            }
            themeIntents.forEach((i)=>{
                if (isThemedIntent(i)) {
                    flatten(i);
                } else {
                    intents.push(i);
                }
            });
        });
    }
    flatten(intent);
    return intents;
}
export function useTheme(registry) {
    const themes = useContext(ThemeContext);
    return useMemo(()=>getIntentsForThemes(themes, registry), [
        registry,
        themes
    ]);
}
export function themed() {
    // TODO(koop): Consider making this a proxied IntentMap to make merging the
    // resulting intents more efficient and/or add intents directly.
    const intent = onComponentWillReceiveProps(function useTheme() {
        const themes = useContext(ThemeContext);
        return useMemo(()=>getIntentsForThemes(themes, intent), [
            themes
        ]);
    });
    intent.byTheme = {};
    return intent;
}
// This is an internal function to create a render intent that only applies
// specific themes from a themed intent. We need this to apply global styles
// only for the immediate themes and none of the parent themes, since they
// have already been applied
function useOnlyThemes(intent, themes) {
    return useMemo(()=>onRender(function useTheme() {
            return useMemo(()=>getIntentsForThemes(themes, intent), []);
        }), [
        intent,
        themes
    ]);
}
export const themeGlobals = themed();
export const themeAttributes = themed();
// TODO(jlongster, SAIL-2644): In our next major version, change this back to
// not combine themes and instead reset everything
const ThemeProvider = createView(function ThemeProvider({ themes, ...props }) {
    const previousThemes = useContext(ThemeContext);
    const combinedThemes = useMemo(()=>previousThemes.concat(themes), [
        previousThemes,
        themes
    ]);
    const key = combinedThemes.map(({ id })=>id).join('---');
    const memoized = useMemo(()=>({
            themes: combinedThemes,
            css: themes.map((theme)=>theme.css.token.assign(null))
        }), // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        key
    ]);
    return /*#__PURE__*/ React.createElement(ThemeContext.Provider, {
        value: memoized.themes
    }, /*#__PURE__*/ React.createElement(View, {
        uses: [
            useOnlyThemes(themeGlobals, themes),
            useOnlyThemes(themeAttributes, themes),
            props,
            ...memoized.css
        ]
    }));
});
export function createThemeProvider(themes) {
    return createView(function Provider(props) {
        return /*#__PURE__*/ React.createElement(ThemeProvider, {
            themes: themes,
            uses: [
                props
            ]
        });
    });
}
const MergeThemeProvider = createView(function ThemeProvider({ themes, ...props }) {
    const previousThemes = useContext(ThemeContext);
    const combinedThemes = useMemo(()=>previousThemes.concat(themes), [
        previousThemes,
        themes
    ]);
    const key = combinedThemes.map(({ id })=>id).join('---');
    const memoized = useMemo(()=>({
            themes: combinedThemes
        }), // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        key
    ]);
    return /*#__PURE__*/ React.createElement(ThemeContext.Provider, {
        value: memoized.themes
    }, /*#__PURE__*/ React.createElement(View, {
        uses: [
            useOnlyThemes(themeGlobals, themes),
            useOnlyThemes(themeAttributes, themes),
            props
        ]
    }));
});
export function createMergeThemeProvider(themes) {
    return createView(function Provider(props) {
        return /*#__PURE__*/ React.createElement(MergeThemeProvider, {
            themes: themes,
            uses: [
                props
            ]
        });
    });
}

//# sourceMappingURL=theme.js.map